import {Channel} from 'pusher-js';
import {inject, Injectable} from '@angular/core';
import {PusherJsService} from '@services/pusher-js/pusher-js.service';
import {Observable, shareReplay} from 'rxjs';
import {CompanyModel, User} from '@core/services';
import {COMMON_DATA_INFO} from '@core/providers/common-data/common-data';
import {distinctUntilChanged, filter, map} from 'rxjs/operators';
import {UUIDType} from '@shared/models';

const buildUserChannel = (companyId: UUIDType, userId: UUIDType): string => `tenants.${companyId}.users.${userId}`;

@Injectable()
export class RealtimeChannelService {
  private readonly _pusherService = inject(PusherJsService);
  readonly _channels = new Map<string, Channel>();

  private _connect(channelName: string): Channel {
    if (!this._channels.has(channelName)) {
      const channel = this._pusherService.pusher.subscribe(channelName);
      this._channels.set(channelName, channel);
    }
    return this._channels.get(channelName);
  }

  listen<T>(channelName: string, eventName: string): Observable<T> {
    return new Observable<T>(observer => {
      const channel = this._connect(channelName);

      const handler = (data: T) => observer.next(data);
      channel.bind(eventName, handler);

      return () => {
        channel.unbind(eventName, handler);
      };
    }).pipe(shareReplay({bufferSize: 1, refCount: true}));
  }

  unsubscribe() {
    this._channels.forEach(channel => {
      this._pusherService.pusher.unsubscribe(channel.name);
    });
    this._channels.clear();
  }
}

@Injectable()
export class UserContextService {
  private readonly _requestCommonData = inject<Observable<[CompanyModel, User]>>(COMMON_DATA_INFO);
  readonly userContext = this._requestCommonData.pipe(
    map(([company, user]) => buildUserChannel(company?.id ?? '', user?.id ?? '')),
    filter(Boolean),
    distinctUntilChanged()
  );
}
